import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Card from "../components/Card";
import { mlmAPI } from "../services/api";

// Recursive Tree Node Component
const TreeNode = ({ node, level = 0 }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "Active":
        return "bg-green-500/20 text-green-400 border border-green-500/30";
      case "Pending":
        return "bg-amber-500/20 text-amber-400 border border-amber-500/30";
      case "Inactive":
        return "bg-red-500/20 text-red-400 border border-red-500/30";
      default:
        return "bg-gray-500/20 text-[var(--text-secondary)] border border-gray-500/30";
    }
  };

  const hasChildren = node.children && node.children.length > 0;

  return (
    <div className="relative">
      {/* Node Card */}
      <div
        className={`mb-2 transition-all duration-300 ${
          level > 0 ? "ml-6 md:ml-8" : ""
        }`}
      >
        <Card className="hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 cursor-pointer p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-1">
              {/* Expand/Collapse Button */}
              {hasChildren && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="w-6 h-6 rounded-lg bg-[var(--bg-card)] border border-primary/20 flex items-center justify-center hover:border-primary transition-all transform hover:scale-110"
                >
                  <svg
                    className={`w-4 h-4 text-[var(--text-primary)] transition-transform duration-300 ${
                      isExpanded ? "rotate-90" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              )}

              {/* Avatar */}
              <div className="w-9 h-9 rounded-full bg-gradient-to-r from-[#22c55e] to-[#16a34a] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                {node.name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")}
              </div>

              {/* User Info */}
              <div className="flex-1 min-w-0">
                <h3 className="text-sm md:text-base font-bold text-[var(--text-primary)] truncate">
                  {node.name}
                </h3>
                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                  <span className="text-xs text-[var(--text-primary)] opacity-60">
                    Level {node.level || 1}
                  </span>
                  <span className="text-xs text-[var(--text-primary)] opacity-40">•</span>
                  <span className="text-xs text-[var(--text-primary)] opacity-60">
                    ${(node.totalEarnings || 0).toFixed(2)}
                  </span>
                  {node.status && (
                    <>
                      <span className="text-xs text-[var(--text-primary)] opacity-40">•</span>
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusBadgeClass(
                          node.status,
                        )}`}
                      >
                        {node.status}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Referrals Count */}
            <div className="text-right">
              <p className="text-xs text-[var(--text-primary)] opacity-60">
                {hasChildren ? "Refs" : "Email"}
              </p>
              <p className="text-sm font-bold text-[var(--text-primary)] truncate max-w-[150px]">
                {hasChildren ? node.children.length : node.email}
              </p>
            </div>
          </div>
        </Card>

        {/* Connector Line */}
        {hasChildren && isExpanded && level < 10 && (
          <div className="ml-3 md:ml-4 border-l-2 border-primary/20 pl-0">
            {/* Children */}
            <div className="space-y-0">
              {node.children.map((child, index) => (
                <TreeNode key={child.id} node={child} level={level + 1} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MLMTeam = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [treeData, setTreeData] = useState(null);

  useEffect(() => {
    fetchTreeData();
  }, []);

  const fetchTreeData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await mlmAPI.getHierarchicalTree();

      console.log("Tree API Response:", response);

      if (response.success && response.data) {
        setTreeData(response.data);
      } else if (response.success && !response.data) {
        // User has no referral tree yet
        setTreeData({
          id: null,
          name: "No Referrals Yet",
          email: "",
          children: [],
          childrenCount: 0,
          totalEarnings: 0,
        });
      } else {
        throw new Error(response.message || "Failed to load tree data");
      }
    } catch (err) {
      console.error("Error fetching tree data:", err);
      console.error("Error response:", err.response);
      const errorMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to load referral tree. Please make sure you are logged in.";
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-[var(--bg-main)]">
        <Sidebar />
        <div className="flex-1 p-4 md:p-8 md:ml-64">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen bg-[var(--bg-main)]">
        <Sidebar />
        <div className="flex-1 p-4 md:p-8 md:ml-64">
          <Card className="text-center py-12">
            <div className="text-red-400 mb-4">
              <svg
                className="w-16 h-16 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-lg font-semibold">{error}</p>
            </div>
            <button
              onClick={fetchTreeData}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white font-semibold hover:shadow-lg hover:shadow-primary/50 transition-all"
            >
              Retry
            </button>
          </Card>
        </div>
      </div>
    );
  }

  if (!treeData) {
    return (
      <div className="flex min-h-screen bg-[var(--bg-main)]">
        <Sidebar />
        <div className="flex-1 p-4 md:p-8 md:ml-64">
          <Card className="text-center py-12">
            <p className="text-[var(--text-primary)] text-lg">No referral tree data available</p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-[var(--bg-main)]">
      <Sidebar />
      <div className="flex-1 p-4 md:p-8 md:ml-64">
        {/* Header */}
        <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
              My Team (Tree View)
            </h1>
            <p className="text-[var(--text-primary)] text-sm md:text-base">
              Visualize your complete referral network hierarchy
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => navigate("/my-direct")}
              className="px-4 py-2 rounded-xl border-2 border-primary text-primary font-semibold hover:bg-[#22c55e]/10 transition-all"
            >
              My Direct Team
            </button>
            <button
              type="button"
              onClick={() => navigate("/mlm-team")}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white font-semibold hover:shadow-lg hover:shadow-primary/30 transition-all"
            >
              My Aiarbix Team
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Filter Panel */}
          <div className="w-full md:w-72 lg:w-80 flex-shrink-0 space-y-4">
            <Card>
              <h2 className="text-xl font-bold mb-4 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                Filters
              </h2>

              {/* Search Input */}
              <div className="mb-4">
                <label className="text-sm text-[var(--text-primary)] opacity-60 mb-2 block">
                  Search
                </label>
                <div className="relative">
                  <svg
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--text-primary)] opacity-60"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search by name or ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-[var(--bg-card)] border border-primary/20 rounded-xl text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:border-primary transition-all"
                  />
                </div>
              </div>

              {/* Note: Filtering will be implemented in future updates */}
            </Card>

            {/* Stats Card */}
            <Card>
              <h3 className="text-lg font-bold text-[var(--text-primary)] mb-4">Team Stats</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center pb-3 border-b border-primary/10">
                  <span className="text-sm text-[var(--text-primary)] opacity-60">
                    Total Members
                  </span>
                  <span className="text-xl font-bold text-[var(--text-primary)]">
                    {treeData.childrenCount || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-primary/10">
                  <span className="text-sm text-[var(--text-primary)] opacity-60">
                    Direct Referrals
                  </span>
                  <span className="text-xl font-bold text-green-400">
                    {treeData.children?.length || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[var(--text-primary)] opacity-60">
                    Total Earnings
                  </span>
                  <span className="text-xl font-bold text-[var(--text-primary)]">
                    ${(treeData.totalEarnings || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </Card>
          </div>

          {/* Right Tree View Panel */}
          <div className="flex-1">
            <Card className="min-h-[600px]">
              <div className="mb-6">
                <h2 className="text-2xl font-bold mb-2 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                  Team Structure
                </h2>
                <p className="text-sm text-[var(--text-primary)] opacity-60">
                  Click arrows to expand/collapse team members
                </p>
              </div>

              {/* Tree Visualization */}
              <div className="relative">
                <TreeNode node={treeData} level={0} />
              </div>
            </Card>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center py-6 mt-8">
          <p className="text-sm text-[var(--text-primary)] opacity-60">
            © 2026 Aiarbix. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MLMTeam;
